// Train seat availability queue.
//
// The form search runs with fetch_avail = 0, so routes stream back fast with no
// live seat data. This module then fetches availability with the GET
// get_avail_only endpoint:
//
//   - One job per train segment (train|from|to|date|quota). Identical legs across
//     routes share one job, so one call fills them all.
//   - Inside a job, classes are checked ONE AT A TIME: preferred class first, then
//     the classes to its right in 1A, EC, EV, 2A, CC, 3A, SL, 2S (cheaper, nearest
//     first), then those to its left (only classes the train has, from
//     segment.seat_classes). The job stops at the first class with is_avail === true.
//   - Calls run in rounds across trains, up to AUTO_CONCURRENCY in parallel: every
//     train's preferred class first, then the next class only for trains still
//     unavailable, and so on. Within a round, the top 12 visible results go first.
//     This gets the whole list a correct first sort as early as possible.
//   - Manual refresh clicks skip the queue and fire at once. Every cell has its own
//     status, so any number of them load in parallel, each with its own loader.
//   - Results are batched into Redux every FLUSH_MS; the reducer re-resolves the
//     shown class and Best / Cheapest Available / Quickest Available re-sort.
//   - A new search (reset) aborts everything in flight and drops late replies.

import { applySegmentAvailability } from '../store/slices/travelSlice';
import { FEATURES } from '../config/features';
import { reqKeyOf, segKeyOf, quotaOf, segDateOf, toDDMMYYYY, classPriorityOf } from '../utils/seatAvailability';

const AVAIL_API = 'https://hh183pxn0f.execute-api.ap-south-1.amazonaws.com/development/search';
const AVAIL_API_KEY = 'web_dev_1';

export const AUTO_AVAIL_ENABLED = FEATURES.AUTO_FETCH_AVAIL !== false;
// Number of trains checked in parallel. Kept under the browser's 6-per-host
// HTTP/1.1 limit so manual refreshes and the calendar-fares call (same host) are
// not stuck behind the auto queue.
const AUTO_CONCURRENCY = FEATURES.AVAIL_CONCURRENCY || 4;
const REQUEST_TIMEOUT_MS = 20000;
const FLUSH_MS = 300;

// Per seat-class cell. key = reqKeyOf(seg, cls)
// { status: 'queued'|'loading'|'done'|'error', seg, cls, controller, lastInfo, waiters }
const records = new Map();
// Per train segment. key = segKeyOf(seg)
// { seg, order: [cls], idx, prio, status: 'queued'|'loading'|'done', found }
const jobs = new Map();

const keyListeners = new Map();
const progressListeners = new Set();

let generation = 0;
let dispatchRef = null;
let preferredClass = null;
let activeAuto = 0;
let resultBuffer = [];
let flushTimer = null;
let progressTimer = null;
let progressSnapshot = { primaryTotal: 0, primaryDone: 0, primaryPending: 0 };

const SEG_PREFIX = 'seg:';

// Status key for a whole train segment (its job), usable with getAvailStatus / useAvailStatuses
export const segAvailKeyOf = (seg) => {
    const k = segKeyOf(seg);
    return k ? SEG_PREFIX + k : null;
};

// ── subscriptions ────────────────────────────────────────────────────────
export const getAvailStatus = (key) => {
    if (!key) return 'idle';
    if (key.startsWith(SEG_PREFIX)) return jobs.get(key.slice(SEG_PREFIX.length))?.status || 'idle';
    return records.get(key)?.status || 'idle';
};

export const subscribeAvailKey = (key, cb) => {
    let set = keyListeners.get(key);
    if (!set) { set = new Set(); keyListeners.set(key, set); }
    set.add(cb);
    return () => {
        set.delete(cb);
        if (set.size === 0) keyListeners.delete(key);
    };
};

export const subscribeAvailProgress = (cb) => {
    progressListeners.add(cb);
    return () => progressListeners.delete(cb);
};

// primaryTotal / primaryDone / primaryPending count train segments
export const getAvailProgress = () => progressSnapshot;

const notifyKey = (key) => {
    const set = keyListeners.get(key);
    if (set) set.forEach((cb) => cb());
};
const notifySeg = (segKey) => notifyKey(SEG_PREFIX + segKey);

const recomputeProgress = () => {
    progressTimer = null;
    let total = 0, done = 0;
    jobs.forEach((j) => { total++; if (j.status === 'done') done++; });
    const p = progressSnapshot;
    if (p.primaryTotal !== total || p.primaryDone !== done) {
        progressSnapshot = { primaryTotal: total, primaryDone: done, primaryPending: total - done };
        progressListeners.forEach((cb) => cb());
    }
};

const scheduleProgress = () => {
    if (!progressTimer) progressTimer = setTimeout(recomputeProgress, 0);
};

// ── setup / reset ────────────────────────────────────────────────────────
export const attachAvailabilityDispatch = (dispatch) => { dispatchRef = dispatch; };

// The user's preferred train class for the current search (e.g. '3A')
export const setAvailPreferredClass = (cls) => { preferredClass = cls || null; };
export const getAvailPreferredClass = () => preferredClass;

// Call at the start of every fresh search
export const resetAvailability = () => {
    generation++;
    records.forEach((r) => { try { r.controller?.abort(); } catch (e) { /* noop */ } });
    records.clear();
    jobs.clear();
    resultBuffer = [];
    if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
    activeAuto = 0;
    Array.from(keyListeners.keys()).forEach(notifyKey);
    recomputeProgress();
};

// ── one GET for one (segment, class) cell ───────────────────────────────
const buildUrl = (seg, cls) => {
    const params = new URLSearchParams({
        train_num: seg.train_num || '',
        src: seg.origin_code || '',
        dest: seg.dest_code || '',
        date: toDDMMYYYY(segDateOf(seg)),
        pref_train_class: cls,
        quota: quotaOf(seg),
        api_key: AVAIL_API_KEY,
        get_avail_only: '1',
    });
    return `${AVAIL_API}?${params.toString()}`;
};

const setCellStatus = (key, status) => {
    const r = records.get(key);
    if (!r) return;
    r.status = status;
    if (status !== 'loading') r.controller = null;
    notifyKey(key);
};

const flush = () => {
    if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
    if (resultBuffer.length === 0) return;
    const batch = resultBuffer;
    resultBuffer = [];
    if (dispatchRef) {
        dispatchRef(applySegmentAvailability({
            pref: preferredClass,
            updates: batch.map(({ segKey, cls, info, manual }) => ({ segKey, cls, info, manual })),
        }));
    }
    // Cell loaders stop in the same tick as the store update, so a cell never
    // drops its loader while still showing the old value.
    batch.forEach((b) => setCellStatus(b.key, 'done'));
};

const queueResult = (item, immediate) => {
    resultBuffer.push(item);
    if (immediate) { flush(); return; }
    if (!flushTimer) flushTimer = setTimeout(flush, FLUSH_MS);
};

const ensureRecord = (key, seg, cls, status) => {
    let r = records.get(key);
    if (!r) {
        r = { status, seg, cls, controller: null, lastInfo: null, waiters: [] };
        records.set(key, r);
        notifyKey(key);
    }
    return r;
};

// Fetches one cell. onSettled(info | null) runs when this cell's answer is known.
// If the cell is already loading (e.g. a manual click), onSettled waits for that
// call instead of firing a duplicate. Returns true if a new request started.
const fetchCell = (key, rec, { manual, onSettled }) => {
    if (onSettled) rec.waiters.push(onSettled);
    if (rec.status === 'loading') return false;

    const gen = generation;
    const controller = new AbortController();
    rec.controller = controller;
    setCellStatus(key, 'loading');
    rec.controller = controller;
    if (!manual) activeAuto++;

    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    fetch(buildUrl(rec.seg, rec.cls), { method: 'GET', signal: controller.signal })
        .then((res) => res.json())
        .then((data) => (data?.status === 200 ? data?.data?.seat_avail_ui || null : null))
        .catch((err) => {
            if (err?.name !== 'AbortError') console.error('Availability fetch error:', err);
            return null;
        })
        .then((info) => {
            clearTimeout(timeoutId);
            if (gen !== generation) return;
            if (!manual) activeAuto = Math.max(0, activeAuto - 1);
            rec.lastInfo = info;
            if (info) queueResult({ key, segKey: segKeyOf(rec.seg), cls: rec.cls, info, manual: !!manual }, manual);
            else setCellStatus(key, 'error');
            const waiters = rec.waiters;
            rec.waiters = [];
            waiters.forEach((cb) => cb(info));
            pump();
        });

    return true;
};

// ── per-train jobs ───────────────────────────────────────────────────────
const finishJob = (segKey, job, found) => {
    job.status = 'done';
    job.found = found;
    // Classes after the one that was found are not checked: drop their loaders
    for (let i = job.idx + 1; i < job.order.length; i++) {
        const key = reqKeyOf(job.seg, job.order[i]);
        const r = records.get(key);
        if (r && r.status === 'queued') {
            records.delete(key);
            notifyKey(key);
        }
    }
    notifySeg(segKey);
    scheduleProgress();
};

// Runs the job's next unchecked class. Classes already answered (a manual refresh
// got there first) are used as they are.
const stepJob = (segKey, job) => {
    const gen = generation;
    while (job.idx < job.order.length) {
        const cls = job.order[job.idx];
        const key = reqKeyOf(job.seg, cls);
        const rec = ensureRecord(key, job.seg, cls, 'queued');

        if (rec.status === 'done' && rec.lastInfo) {
            if (rec.lastInfo.is_avail === true) { finishJob(segKey, job, true); return; }
            job.idx++;
            continue;
        }

        job.status = 'loading';
        notifySeg(segKey);
        fetchCell(key, rec, {
            manual: false,
            onSettled: (info) => {
                if (gen !== generation) return;
                if (info && info.is_avail === true) { finishJob(segKey, job, true); return; }
                job.idx++;
                if (job.idx >= job.order.length) { finishJob(segKey, job, false); return; }
                job.status = 'queued';
                notifySeg(segKey);
            },
        });
        return;
    }
    finishJob(segKey, job, false);
};

const pump = () => {
    let guard = 0;
    while (activeAuto < AUTO_CONCURRENCY && guard++ < 10000) {
        let bestKey = null;
        let best = null;
        // Round (how many classes this train has already checked) first, then rank
        const rankOf = (j) => j.idx * 1e8 + j.prio;
        jobs.forEach((j, k) => {
            if (j.status === 'queued' && (!best || rankOf(j) < rankOf(best))) { best = j; bestKey = k; }
        });
        if (!best) return;
        stepJob(bestKey, best);
    }
};

// items: [{ seg, prio }] one per train segment (lower prio runs first)
export const enqueueAutoAvailability = (items) => {
    let added = false;
    items.forEach(({ seg, prio }) => {
        const segKey = segKeyOf(seg);
        if (!segKey) return;
        const existing = jobs.get(segKey);
        if (existing) {
            if (existing.status === 'queued' && prio < existing.prio) existing.prio = prio;
            return;
        }
        const order = classPriorityOf(seg, preferredClass);
        if (order.length === 0) return;
        jobs.set(segKey, { seg, order, idx: 0, prio, status: 'queued', found: false });
        // Every class the job may check shows a loader until it is checked or skipped
        order.forEach((cls) => ensureRecord(reqKeyOf(seg, cls), seg, cls, 'queued'));
        notifySeg(segKey);
        added = true;
    });
    if (added) scheduleProgress();
    pump();
};

// Manual refresh from a seat-class cell. Fires immediately, in parallel with
// everything else. A cell already loading is left alone.
export const requestAvailability = (seg, cls) => {
    const key = reqKeyOf(seg, cls);
    if (!key) return;
    const rec = ensureRecord(key, seg, cls, 'idle');
    if (rec.status === 'loading') return;
    fetchCell(key, rec, { manual: true, onSettled: null });
};
